import { MailOutlined, PaperClipOutlined } from '@ant-design/icons';
import { Card, Divider, Timeline, Typography } from 'antd';
import moment from 'moment';
import { Trans, useTranslation } from 'react-i18next';

import useAPIQuery from '../../api/useAPIQuery';
import { EmailCommunicationDetailSerializer } from '../../types/api';
import style from './ApplicantMindsightMessageTimeline.module.css';

interface AttachmentLinkProps {
  attachmentId: number;
}

const AttachmentLink = ({ attachmentId }: AttachmentLinkProps) => {
  const attachment = useAPIQuery(`/attachments/${attachmentId}/`);

  return (
    <Typography.Paragraph>
      <Typography.Link href={attachment.data?.file ?? ''} target="_blank">
        <PaperClipOutlined /> {attachment.data?.filename}
      </Typography.Link>
    </Typography.Paragraph>
  );
};

interface ApplicantMessageTimelineProps {
  opportunityTitle?: string;
  recruiterName?: string;
  createdAt: string;
  emailCommunication: EmailCommunicationDetailSerializer;
}

const ApplicantMessageTimeline = ({
  emailCommunication,
  opportunityTitle,
  recruiterName,
  createdAt,
}: ApplicantMessageTimelineProps) => {
  const { t } = useTranslation();

  const automaticSentString = recruiterName ? t('enviou') : t('enviou automaticamente');

  return (
    <Timeline.Item dot={<MailOutlined />}>
      <Card>
        <Typography.Paragraph>
          <Typography.Text type="secondary">{moment(createdAt).format('LLL')}</Typography.Text>
          <br />
          <Typography.Text strong> {recruiterName || <Trans>O sistema</Trans>} </Typography.Text>
          <Typography.Text>
            {emailCommunication.type === 'COMMUNICATION' && (
              <span>
                {automaticSentString} <Trans>uma</Trans>{' '}
                <Typography.Text strong>
                  <Trans>comunicação</Trans>
                </Typography.Text>
              </span>
            )}
            {emailCommunication.type === 'PRACTICAL_TECHNICAL_CASE' && (
              <span>
                {automaticSentString} <Trans>um</Trans>{' '}
                <Typography.Text strong>
                  <Trans>case prático ou técnico</Trans>
                </Typography.Text>
              </span>
            )}
            {emailCommunication.type === 'TESTS' && (
              <span>
                {automaticSentString} <Trans>os</Trans>{' '}
                <Typography.Text strong>
                  <Trans>testes</Trans>
                </Typography.Text>
              </span>
            )}
            {emailCommunication.type === 'ADDITIONAL_QUESTIONS' && (
              <span>
                {automaticSentString}{' '}
                <Typography.Text strong>
                  <Trans>perguntas de pré-requisito</Trans>
                </Typography.Text>
              </span>
            )}
            {emailCommunication.type === 'CUSTOM_TESTS' && (
              <span>
                {automaticSentString} <Trans>os</Trans>{' '}
                <Typography.Text strong>
                  <Trans>testes customizados</Trans>
                </Typography.Text>
              </span>
            )}
            {emailCommunication.type === 'WELCOME_MESSAGE' && (
              <span>
                {automaticSentString} <Trans>uma</Trans>{' '}
                <Typography.Text strong>
                  <Trans>mensagem de boas vindas</Trans>
                </Typography.Text>
              </span>
            )}
            <span>
              , <Trans>via e-mail, para o(a) candidato(a)</Trans>
            </span>
            {opportunityTitle && (
              <span>
                {' '}
                <Trans>na vaga</Trans> <Typography.Text strong>{opportunityTitle}</Typography.Text>
              </span>
            )}
          </Typography.Text>
        </Typography.Paragraph>
        <Divider />
        <div
          dangerouslySetInnerHTML={{ __html: emailCommunication.message || '' }}
          className={style.emailMessage}
        />
        {emailCommunication.type !== 'TESTS' &&
          emailCommunication.attachments.map((attachmentId) => (
            <AttachmentLink attachmentId={attachmentId} />
          ))}
      </Card>
    </Timeline.Item>
  );
};

export default ApplicantMessageTimeline;
